import React, { useState, useContext, useEffect } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { AuthContext } from '~/context/auth';
import { RegistrationContext } from '~/context/registration';
import { CampaignContext } from '../../context/campaign';
import toast from 'react-hot-toast';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { campaignPatientDetails } from '~/services/registration';
import { fetchPatient, fetchMyPerson } from '~/services/campaign';

import {
    TextInput,
    SelectInput,
    PhoneInput,
    phoneRegExp,
    stateOptions,
} from '~/shared/components/form/Inputs';
import { CheckBox } from '~/shared/components/form/CheckBox';
import { ProgressBar } from './ProgressBar';
import {
    languageOptions,
    genderOptions,
    maritalStatusOptions,
    ethnicityOptions,
} from '~/shared/config';
import {
    Container,
    Button,
    Card,
    CardBody,
    CardText,
    CardTitle,
    Col,
    Row,
    Form,
    FormGroup,
    Input,
    Label,
} from 'reactstrap';

import { SelectOption } from '~/types/globals';
import { Loading } from '../../shared/components/Loading';
import { fetchCountries } from '../../services/hhl';

const defaultValues = {
    first_name: '',
    last_name: '',
    nickname: '',
    phone_number: '',
    email: '',
    zip_code: '',
    preferred_language: '',
    gender: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    country: '',
};

const validationSchema = Yup.object().shape({
    first_name: Yup.string().required('Required'),
    last_name: Yup.string().required('Required'),
    nickname: Yup.string(),
    phone_number: Yup.string()
        .required('Required')
        .matches(phoneRegExp, 'Phone number is not valid'),
    email: Yup.string().required('Required').email('Must be a valid email'),
    zip_code: Yup.string().required('Required'),
});

export const PatientDetails = ({}: any) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [{ campaignRequest }, registrationDispatch] =
        useContext(RegistrationContext);
    const [{ user }, userDispatch] = useContext(AuthContext);
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const person = campaign?.person;
    const [useSameContact, setUseSameContact] = useState(false);
    const [httpError, setHttpError] = useState('');
    const [countries, setCountries] = useState<array>([]);

    const [patient, setPatient] = useState(null);
    const [languageOption, setLanguageOption] = useState<string>();
    const {
        register,
        handleSubmit,
        watch,
        control,
        reset,
        setValue,
        getValues,
        formState: { isSubmitting, isValid, errors },
    } = useForm({
        defaultValues,
        mode: 'onBlur',
        resolver: yupResolver(validationSchema),
    });
    const location = useLocation();
    const history = useHistory();

    useEffect(() => {
        if (languageOption && languageOption !== 'other') {
            setValue('preferred_language', languageOption);
        }
    }, [languageOption]);

    const getPerson = async () => {
        const resp = await fetchMyPerson();
        campaignDispatch({
            type: 'set_person',
            payload: resp,
        });
        setLoading(false);
    };

    useEffect(() => {
        const { data } = location.state;
        const { first_name, last_name, nickname } = data;
        console.log('first_name: ', first_name);
        if (!first_name || !last_name) {
            // Go to previous step
            history.push('/get-started/patient/who');
        } else {
            reset({ ...data, first_name, last_name, nickname });
            if (!person) {
                getPerson();
            } else {
                setLoading(false);
            }
        }

        let resp = fetchCountries().then((res) => {
            const opts = res.map((opt, i) => {
                return {
                    label: opt.name,
                    value: opt.id,
                };
            });
            setCountries(opts);
        });
    }, []);

    const handleSameContactCheck = () => {
        const { data } = location.state;
        const { first_name, last_name, nickname } = data;
        if (!person) {
            return;
        }
        const { phone, email, zip_code, preferred_language } = person;
        setLanguageOption(preferred_language);
        reset({
            phone_number: phone,
            email,
            zip_code,
            first_name,
            last_name,
            nickname,
        });
    };

    useEffect(() => {
        if (useSameContact) {
            handleSameContactCheck();
        } else {
            setLanguageOption(null);
            const { data } = location.state;
            const { first_name, last_name, nickname } = data;
            reset({ ...defaultValues, first_name, last_name, nickname });
        }
    }, [useSameContact]);

    const onSubmit = async (data) => {
        try {
            const resp = await campaignPatientDetails(data);
            if (resp.error) {
                setHttpError(resp.error);
            } else {
                registrationDispatch({
                    type: 'set_patient',
                    patient: resp,
                });
                history.push('/get-started/condition/type');
            }
        } catch (e) {
            console.log('e: ', e);
        }
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <Container>
            <ProgressBar selectedStep='patient' />

            <Row className='justify-content-center py-5'>
                <Col md='6'>
                    <div className='text-center'>
                        <h3>
                            What is the best way to contact{' '}
                            {getValues('first_name')}?
                        </h3>
                    </div>

                    <Card
                        className='box-shadow my-5'
                        style={{
                            maxWidth: '400px',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                        }}
                    >
                        <CardBody>
                            <Form onSubmit={handleSubmit(onSubmit)}>
                                <p style={{ color: 'red' }}>{httpError}</p>
                                <Label check>
                                    <Input
                                        id={`same-info`}
                                        type='checkbox'
                                        onChange={() =>
                                            setUseSameContact(!useSameContact)
                                        }
                                        name={`same-info`}
                                    />
                                    Use my contact information for{' '}
                                    {getValues('first_name')}
                                </Label>
                                <PhoneInput
                                    name='phone_number'
                                    label='Phone Number'
                                    control={control}
                                    errors={errors?.phone_number?.message}
                                    required={true}
                                    ariaRequired={true}
                                />
                                <TextInput
                                    name='email'
                                    type='email'
                                    label='Email'
                                    control={control}
                                    errors={errors?.email?.message}
                                    required={true}
                                    ariaRequired={true}
                                />
                                <FormGroup tag='fieldset' className='mb-3'>
                                    <Label>Preferred Language</Label>
                                    <FormGroup check>
                                        {languageOptions.map((l, i) => {
                                            return (
                                                <div key={i}>
                                                    <Label check>
                                                        <Input
                                                            type='radio'
                                                            onChange={() =>
                                                                setLanguageOption(
                                                                    l.value
                                                                )
                                                            }
                                                            name='preferred_language'
                                                            checked={
                                                                languageOption ===
                                                                l.value
                                                            }
                                                        />
                                                        {l.label}
                                                    </Label>
                                                </div>
                                            );
                                        })}
                                    </FormGroup>

                                    {languageOption === 'other' && (
                                        <Input
                                            name='preferred_language'
                                            required={true}
                                            aria-required={true}
                                            onChange={(e) =>
                                                setValue(
                                                    'preferred_language',
                                                    e.target.value
                                                )
                                            }
                                        />
                                    )}
                                </FormGroup>
                                <h4>Contact Information</h4>
                                <SelectInput
                                    control={control}
                                    name='gender'
                                    label='Gender'
                                    options={genderOptions}
                                    errors={errors?.gender}
                                    required={true}
                                    ariaRequired={true}
                                />
                                <TextInput
                                    name='birth_date'
                                    label='Date of Birth'
                                    control={control}
                                    type='date'
                                    errors={errors?.birth_date?.message}
                                    required={true}
                                    ariaRequired={true}
                                />
                                <SelectInput
                                    control={control}
                                    name='marital_status'
                                    label='Martial Status'
                                    options={maritalStatusOptions}
                                    errors={errors?.marital_status}
                                    required={true}
                                    ariaRequired={true}
                                />
                                <h4>Address</h4>
                                <TextInput
                                    name='address_1'
                                    type='text'
                                    label='Street Address 1'
                                    control={control}
                                    errors={errors?.address_1?.message}
                                    required={true}
                                    ariaRequired={true}
                                />
                                <TextInput
                                    name='address_2'
                                    type='text'
                                    label='Street Address 2'
                                    control={control}
                                    errors={errors?.address_2?.message}
                                    required={false}
                                    ariaRequired={false}
                                />
                                <TextInput
                                    name='city'
                                    type='text'
                                    label='City'
                                    control={control}
                                    errors={errors?.city?.message}
                                    required={true}
                                    ariaRequired={true}
                                />
                                <SelectInput
                                    control={control}
                                    name='state'
                                    label='State'
                                    defaultLabel='Select State'
                                    options={stateOptions}
                                    errors={errors?.state}
                                    required={true}
                                    ariaRequired={true}
                                />
                                <TextInput
                                    name='zip_code'
                                    type='text'
                                    label='Zip Code'
                                    control={control}
                                    errors={errors?.zip_code?.message}
                                    required={false}
                                />
                                <SelectInput
                                    control={control}
                                    required={true}
                                    ariaRequired={true}
                                    name='country'
                                    label='Country'
                                    defaultLabel='Select Country'
                                    options={countries}
                                    errors={errors?.country?.message}
                                />
                                <div className='text-center'>
                                    <Button
                                        color='primary'
                                        type='submit'
                                        disabled={!isValid || isSubmitting}
                                    >
                                        {isSubmitting ? `Saving...` : `Next`}
                                    </Button>
                                </div>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};
